/**
 * SectionPreviewPane — preview lateral derecho del Designer.
 *
 * **Recursivo + heading combinado (Opción 4):** camina el árbol completo
 * y muestra cómo se va a ver el artifact en runtime cuando el usuario lo
 * llene. Para cada tipo de nodo:
 *
 *   - `fixed_heading` no repetible (chapter) → render como heading +
 *      recursión sobre hijos.
 *   - `fixed_heading` repetible → SIN heading propio (su identidad vive
 *      en el prefijo de cada card). Renderiza 2 cards de instancia + el
 *      botón "+ Añadir <Tipo>". Cada card se rotula con:
 *         - "<Tipo>: <valor del primer hijo de texto>" (Modo A)
 *         - "<Tipo> #N" cuando no hay primer hijo de texto (Modo B)
 *   - `fixed_block` → callout con el `body` declarado en i18n.
 *   - hojas → render con value de ejemplo (o valueOverride desde la
 *      card padre cuando son el primer hijo de texto de una instancia).
 *
 * Cero drift: usa el mismo `SectionRenderer` del official viewer (B.6.a).
 *
 * Bloque inferior con stats agregados (recorren todo el árbol):
 *   - Nodos totales · Repetibles · Idiomas · Tipos de bloque distintos
 */

import { Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type {
  ArtifactStructuralSection,
  TableColumn,
} from '../../../api/templates'
import { SectionRenderer } from '../../projects/official-viewer/SectionRenderer'
import type { TemplateSectionMeta } from '../../projects/official-viewer/section_types'
import {
  findSectionI18n,
  isContainerType,
  type DesignerState,
} from './state'

interface SectionPreviewPaneProps {
  state: DesignerState
}

export function SectionPreviewPane({ state }: SectionPreviewPaneProps) {
  const { t } = useTranslation('templates')
  return (
    <article className="space-y-4">
      <header className="border-b border-line pb-3 mb-2">
        <h2 className="font-display text-xl font-semibold text-ink">
          {t('designer.preview.title')}
        </h2>
        <p className="mt-1 text-xs text-ink-subtle">
          {t('designer.preview.subtitle')}
        </p>
      </header>

      {state.definition.sections.length === 0 ? (
        <div className="rounded-md border border-dashed border-line p-6 text-center">
          <p className="font-body text-sm text-ink-muted">
            {t('designer.preview.no_section')}
          </p>
        </div>
      ) : (
        state.definition.sections.map((node) => (
          <PreviewNode key={node.section_id} state={state} node={node} />
        ))
      )}
    </article>
  )
}

// ─── Nodo recursivo ────────────────────────────────────────────────────────
function PreviewNode({
  state,
  node,
  /** Override del valor del nodo en este render — usado para mostrar
   * que el "primer hijo de texto" de una instancia toma el ejemplo de
   * la card correspondiente. */
  valueOverride,
  /** Override del título visible — usado dentro de cards de instancia
   * cuando queremos que el primer hijo de texto muestre su título sin
   * repetir el del grupo padre. */
  titleOverride,
}: {
  state: DesignerState
  node: ArtifactStructuralSection
  valueOverride?: unknown
  titleOverride?: string
}) {
  const { t } = useTranslation('templates')
  const i18nSection = findSectionI18n(
    state.definition,
    node.section_id,
    state.activeLocale,
  )
  const title =
    titleOverride ?? i18nSection?.title ?? node.section_id

  // ─── Container repetible: 2 instancias con heading "Tipo: Nombre" ─
  if (isContainerType(node.type) && node.repeatable) {
    const firstTextChild = node.children?.find(
      (c) => c.type === 'plain_text' || c.type === 'rich_text',
    )
    // Título visible del primer hijo de texto (lo que el editor mostraría
    // como label del input). Se usa en el placeholder de cada instancia.
    const firstTextChildTitle = firstTextChild
      ? findSectionI18n(
          state.definition,
          firstTextChild.section_id,
          state.activeLocale,
        )?.title ?? firstTextChild.section_id
      : null

    // Por cada instancia armamos:
    //  - cardHeading: el rótulo visible de la card.
    //  - sampleValue: el valor que va a aparecer dentro del primer hijo
    //    de texto (override). Lo hacemos coincidir con la parte
    //    sustituible del cardHeading para que la equivalencia sea visible.
    const buildSample = (idx: number): {
      cardHeading: string
      sampleValue: string | null
    } => {
      const n = idx + 1
      if (firstTextChild && firstTextChildTitle) {
        // Modo A — heading "Tipo: <placeholder>".
        const sampleValue = t('designer.preview.sample_value_placeholder', {
          field: firstTextChildTitle,
          n,
        })
        return {
          cardHeading: t('designer.preview.instance_heading_with_label', {
            type: title,
            value: sampleValue,
          }),
          sampleValue,
        }
      }
      // Modo B — heading "Tipo #N".
      return {
        cardHeading: t('designer.preview.instance_heading_numbered', {
          type: title,
          n,
        }),
        sampleValue: null,
      }
    }

    return (
      <section>
        {/* Hint sutil para que el user vea que la card hereda del primer
            hijo de texto cuando aplica. NO hay heading propio del grupo
            repetible — su identidad vive en el prefijo de cada card. */}
        {firstTextChildTitle ? (
          <p className="text-[11px] text-ink-subtle italic mb-2">
            {t('designer.preview.repeatable_hint_modeA', {
              type: title,
              field: firstTextChildTitle,
            })}
          </p>
        ) : (
          <p className="text-[11px] text-ink-subtle italic mb-2">
            {t('designer.preview.repeatable_hint_modeB', { type: title })}
          </p>
        )}
        <div className="space-y-3">
          {[0, 1].map((idx) => {
            const { cardHeading, sampleValue } = buildSample(idx)
            return (
              <article
                key={idx}
                className="rounded-md border border-line bg-surface-muted/30 p-3"
              >
                <header className="mb-2 flex items-baseline gap-2">
                  <h4 className="font-display text-sm font-semibold text-ink">
                    {cardHeading}
                  </h4>
                </header>
                <div className="space-y-3 pl-4 border-l-2 border-line">
                  {(node.children ?? []).map((child) => {
                    const isFirstText =
                      firstTextChild !== undefined &&
                      child.section_id === firstTextChild.section_id
                    return (
                      <PreviewNode
                        key={`${child.section_id}-${idx}`}
                        state={state}
                        node={child}
                        {...(isFirstText && sampleValue !== null
                          ? { valueOverride: sampleValue }
                          : {})}
                      />
                    )
                  })}
                </div>
              </article>
            )
          })}
        </div>
        <button
          type="button"
          disabled
          className="mt-3 inline-flex items-center gap-1.5 text-xs text-ink-muted border border-dashed border-line rounded-md px-3 py-1.5 cursor-not-allowed"
          title={t('designer.preview.add_disabled_hint')}
        >
          <Plus className="w-3.5 h-3.5" />
          {t('designer.preview.add_instance', { label: title })}
        </button>
      </section>
    )
  }

  // ─── Container no repetible: heading + recursión ──────────────────
  if (isContainerType(node.type)) {
    return (
      <section className="rounded-md border border-line bg-canvas p-4">
        <header className="mb-3">
          <h3 className="font-display text-base font-semibold text-ink">
            {title}
          </h3>
        </header>
        <div className="space-y-3 pl-4 border-l-2 border-line">
          {(node.children ?? []).map((child) => (
            <PreviewNode
              key={child.section_id}
              state={state}
              node={child}
            />
          ))}
        </div>
      </section>
    )
  }

  // ─── Hoja: usa el SectionRenderer con valor de ejemplo ────────────
  // fixed_block usa el body i18n como value; structured_table genera
  // rows de ejemplo según las columnas declaradas; el resto, ejemplo
  // por tipo.
  let value: unknown
  if (valueOverride !== undefined) {
    value = valueOverride
  } else if (node.type === 'fixed_block') {
    value = {
      body:
        i18nSection?.body ??
        t('designer.preview.fixed_block_placeholder'),
    }
  } else if (node.type === 'structured_table') {
    // El schema vive en `node.table_config.columns[]` (Doc 24 v2).
    const v2Cols = node.table_config?.columns ?? null
    if (v2Cols && v2Cols.length > 0) {
      value = {
        rows: [
          buildSampleStructuredRowV2(v2Cols, 0),
          buildSampleStructuredRowV2(v2Cols, 1),
        ],
      }
    } else {
      value = { rows: [] }
    }
  } else {
    value = buildExampleValue(node.type)
  }

  // ColumnLabels para el SectionRenderer. v2: derivado de
  // `table_config.columns[].i18n[locale].label`. v1 legacy:
  // `i18nSection.column_labels` (mapa key→label).
  let columnLabels: Record<string, string> | undefined
  let metaForRenderer: TemplateSectionMeta = node as TemplateSectionMeta
  if (node.type === 'structured_table' && node.table_config) {
    const labels: Record<string, string> = {}
    for (const col of node.table_config.columns) {
      const label = col.i18n[state.activeLocale]?.label
      if (label) labels[col.key] = label
    }
    columnLabels = labels
    // Adaptar `meta.columns` al shape lite que espera el SectionRenderer
    // (key + type + options). Sin esta adaptación, el renderer ve
    // `columns` undefined (v2 vive en `table_config.columns`) y muestra
    // FallbackJson.
    metaForRenderer = {
      ...(node as TemplateSectionMeta),
      columns: node.table_config.columns.map((col) => ({
        key: col.key,
        type: col.type,
        required: col.required ?? false,
        options:
          col.type === 'enum'
            ? (col.enum_values ?? []).map(
                (ev) => ev.i18n[state.activeLocale]?.label ?? ev.value,
              )
            : undefined,
      })),
    } as TemplateSectionMeta
  } else if (
    typeof i18nSection?.column_labels === 'object' &&
    i18nSection.column_labels !== null
  ) {
    columnLabels = i18nSection.column_labels as Record<string, string>
  }

  return (
    <SectionRenderer
      meta={metaForRenderer}
      title={title}
      value={value}
      {...(columnLabels ? { columnLabels } : {})}
    />
  )
}

/** Construye una fila de ejemplo para un structured_table shape v2
 * (Doc 24). Equivalente a `buildSampleStructuredRow` pero leyendo desde
 * `TableColumn` (con `enum_values[]` en lugar de `options[]` y
 * `type: 'plain_text'|'enum'|'number'|'date'`). */
function buildSampleStructuredRowV2(
  columns: TableColumn[],
  rowIdx: number,
): Record<string, unknown> {
  const row: Record<string, unknown> = {}
  for (const col of columns) {
    if (col.type === 'plain_text') {
      row[col.key] = rowIdx === 0 ? 'Texto de ejemplo 1' : 'Texto de ejemplo 2'
    } else if (col.type === 'number') {
      row[col.key] = rowIdx === 0 ? 42 : 7
    } else if (col.type === 'date') {
      row[col.key] = rowIdx === 0 ? '2026-01-15' : '2026-02-15'
    } else if (col.type === 'enum') {
      const opts = col.enum_values ?? []
      if (opts.length === 0) {
        row[col.key] = '—'
      } else {
        // Devolvemos el `value` estable; el renderer puede mapearlo a
        // label via `columnLabels` o mostrarlo crudo si no hay map.
        row[col.key] = opts[rowIdx % opts.length]?.value ?? '—'
      }
    }
  }
  return row
}

/** Construye un value de ejemplo para mostrar el render del nodo en el
 * preview. Cada block type recibe un value canónico. Los tipos no
 * soportados caen al placeholder honesto del SectionRenderer via
 * `unsupported`. */
function buildExampleValue(type: string): unknown {
  switch (type) {
    case 'rich_text':
      return 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.\n\nUsa este bloque para texto extenso con párrafos.'
    case 'plain_text':
      return 'Texto plano de ejemplo en una línea.'
    case 'bullet_list':
    case 'numbered_list':
      return ['Primer ítem de ejemplo', 'Segundo ítem', 'Tercer ítem']
    case 'checklist':
      return [
        { text: 'Tests verde', done: true },
        { text: 'PR aprobado por Tech Lead', done: true },
        { text: 'Docs actualizadas', done: false },
        { text: 'Deploy a staging', done: false },
      ]
    case 'key_value_grid':
      return [
        { key: 'Sponsor', value: 'María Pérez' },
        { key: 'Cliente', value: 'Acme S.A.' },
        { key: 'Inicio', value: '15-mar-2026' },
      ]
    case 'structured':
      return { example_field: 'value', another: 42, nested: { ok: true } }
    case 'code_block':
      return {
        language: 'typescript',
        code: "function greet(name: string): string {\n  return `Hola, ${name}`\n}\n\nconst msg = greet('HIVA')",
      }
    case 'structured_table':
      // Para structured_table el value canónico es { rows: [...] }; las
      // columnas viven en el meta (declaradas por el template author en
      // el Designer). Si todavía no hay columnas, el renderer cae a
      // FallbackJson — esto es el "empty state" del preview hasta que
      // el author defina el schema.
      return { rows: [] }
    case 'diagram':
    case 'mermaid_flowchart':
      return `flowchart LR
  A[Discovery] --> B[Diseño]
  B --> C[Construcción]
  C --> D[QA]
  D --> E((Entrega))`
    case 'mermaid_sequence':
      return `sequenceDiagram
  participant U as Usuario
  participant A as Auth
  U->>A: POST /login
  A-->>U: 200 OK
  U->>A: GET /me
  A-->>U: { user data }`
    case 'mermaid_use_case':
      return `flowchart TB
  Usuario((Usuario))
  uc1[Iniciar sesión]
  uc2[Recuperar contraseña]
  uc3[Cerrar sesión]
  Usuario --- uc1
  Usuario --- uc2
  Usuario --- uc3`
    case 'mermaid_activity':
      return `flowchart TB
  start((Inicio)) --> step1[Validar inputs]
  step1 --> dec{¿OK?}
  dec -->|sí| step2[Procesar]
  dec -->|no| step1
  step2 --> done((Fin))`
    case 'mermaid_state':
      return `stateDiagram-v2
  [*] --> Idle
  Idle --> Active: start
  Active --> Idle: stop
  Active --> [*]: kill`
    case 'mermaid_er':
      return `erDiagram
  USER ||--o{ POST : escribe
  USER {
    int id
    string name
  }
  POST {
    int id
    string title
  }`
    case 'mermaid_class':
      return `classDiagram
  class Animal {
    +String name
    +eat()
  }
  Animal <|-- Dog
  Animal <|-- Cat`
    case 'mermaid_gantt':
      return `gantt
  title Cronograma del proyecto
  dateFormat YYYY-MM-DD
  section Diseño
    Discovery: 2026-01-01, 5d
    Wireframes: after Discovery, 3d
  section Construcción
    Backend: after Wireframes, 10d`
    case 'mermaid_journey':
      return `journey
  title Onboarding del usuario
  section Registro
    Crear cuenta: 5: Usuario
    Verificar email: 3: Usuario
  section Primer uso
    Crear primer proyecto: 4: Usuario`
    case 'mermaid_mindmap':
      return `mindmap
  root((HIVA))
    Diseño
      Templates
      Lifecycles
    Runtime
      Official
      Drafts`
    case 'mermaid_generic':
      return `flowchart LR
  A --> B
  B --> C`
    default:
      return undefined
  }
}
